import { reactive } from 'vue';
import type { Reactive } from 'vue';
import { useRouter } from 'vue-router';
import { createInjection } from 'src/lib/createInjection';
import masterApi from 'src/apis/master';
import { convKiloposts } from 'src/lib/kilopostHelper';
import type { GeoConnection, MasterData, Lovs } from 'src/models';
import type { RoadNameRaw } from 'src/models/apis/master/masterResponse';
import type {
  Lov,
  CommentType,
  RoadName,
  RoadNameDisp,
  MastersContext,
} from 'src/models/master';
import {
  toLov,
  toRoadName,
  toCommentType,
  toGeoConnections,
} from 'src/models/apis/master/masterResponse';
import type { ShareScopeLov, User } from 'src/models/user';

interface UseReactiveMastersContext {
  masters: Reactive<MastersContext>;
  resolveMastersPromise: () => Promise<void>;
  // 付箋種別の編集後、再読み込みなしで他画面（地図）へ反映するためのフック
  refreshCommentType: (commentTypes: CommentType[]) => void;
}

const { provide, inject } = createInjection<UseReactiveMastersContext>('useMasters');

type RoadNameCollections = {
  roadNames: RoadName[];
  roadNameDisps: RoadNameDisp[];
  roadNameDispMap: Record<string, RoadNameDisp>;
  roadNameToRoadNameDispMap: Record<string, string>;
}

function buildRoadNameCollections(origRoadNames: RoadNameRaw[]): RoadNameCollections {
  const roadNames = origRoadNames.map(e => toRoadName({
    ...e,
    directions: e.directions.split(','),
  }));

  // roadNameDispごとにまとめたものも作る（e は RoadNameRaw = snake_case）
  const roadNameDispMap = origRoadNames.reduce<Record<string, RoadNameDisp>>((acc, e) => {
    const parsedDirections = e.directions.split(',');
    const existing = acc[e.road_name_disp];
    if (!existing) {
      acc[e.road_name_disp] = {
        area: e.area,
        roadNameDisp: e.road_name_disp,
        roadNameDispShort: e.road_name_disp_short,
        roadNames: [e.road_name],
        directions: parsedDirections,
      };
      return acc;
    }
    existing.roadNames.push(e.road_name);
    existing.directions.push(...parsedDirections);
    return acc;
  }, {});

  // directionsをuniqueする
  Object.values(roadNameDispMap).forEach(d => {
    d.directions = [...new Set(d.directions)];
  });

  const roadNameDisps = Object.values(roadNameDispMap);
  const roadNameToRoadNameDispMap = roadNameDisps.reduce<Record<string, string>>((acc, disp) => {
    disp.roadNames.forEach(roadName => { acc[roadName] = disp.roadNameDisp; });
    return acc;
  }, {});

  return { roadNames, roadNameDisps, roadNameDispMap, roadNameToRoadNameDispMap };
}

function buildCommentTypeValsMap(commentTypes: CommentType[]): MasterData['commentType'] {
  return {
    vals: commentTypes,
    map: commentTypes.reduce<Record<string, CommentType>>((acc, e) => {
      acc[e.commentType] = e; return acc;
    }, {}),
  };
}

export function provideMasters(user: User): void {
  const router = useRouter();

  const mastersPromise = (async(): Promise<MasterData> => {
    const { data: masters } = await masterApi.getAll();

    const shareScopeLovs = user.settings.shareScopeLov;
    const carKindLovs = masters.lovs.car_kind.map(toLov);
    const roleLovs = masters.lovs.role.map(toLov);
    const lovs: Lovs = {
      carKind: {
        vals: carKindLovs,
        map: carKindLovs.reduce<Record<string, Lov>>((acc, lov) => {
          acc[lov.key] = lov; return acc;
        }, {}),
      },
      role: {
        vals: roleLovs,
        map: roleLovs.reduce<Record<string, Lov>>((acc, lov) => {
          acc[lov.key] = lov; return acc;
        }, {}),
      },
      shareScope: {
        vals: shareScopeLovs,
        map: shareScopeLovs.reduce<Record<string, ShareScopeLov>>((acc, lov) => {
          acc[lov.key] = lov; return acc;
        }, {}),
      },
    };

    const { kpMapByRoadnameDirectionPlace, kpMapByKpUid } = convKiloposts({
      kiloposts: masters.kiloposts,
      kilopost_col_map: masters.kilopost_col_map,
      kilopost_val_map: masters.kilopost_val_map,
    });
    const roadNameCollections = buildRoadNameCollections(masters.road_names);
    const commentType = buildCommentTypeValsMap(masters.comment_types.map(toCommentType));
    const commentTypeIconPaths = Object.values(masters.comment_type_icon_paths);
    const commentFeaturesBySourceType = masters.comment_features_by_source_type;

    // 道路の接続情報
    let geoConnections: Record<string, Record<string, GeoConnection>> = {};
    try {
      const mod = await import(`src/data/geo_connections_${user.settings.kpSetName}.json`);
      geoConnections = toGeoConnections(mod.default);
    } catch {}

    return {
      lovs,
      kpMap: kpMapByRoadnameDirectionPlace,
      kpMapByKpUid,
      commentTypeIconPaths,
      commentFeaturesBySourceType,
      roadNames: roadNameCollections.roadNames,
      roadNameDisps: roadNameCollections.roadNameDisps,
      roadNameDispMap: roadNameCollections.roadNameDispMap,
      roadNameToRoadNameDispMap: roadNameCollections.roadNameToRoadNameDispMap,
      commentType,
      geoConnections,
    };
  })();

  // refreshCommentType による更新を全画面へ反映させるため、provide 側で1つだけ生成して共有する。
  const masters = reactive<MastersContext>({
    kp: {
      kpMap: new Map(),
      kpMapByKpUid: new Map(),
      roadNames: [],
      roadNameDisps: [],
      roadNameDispMap: {},
      roadNameToRoadNameDispMap: {},
      geoConnections: {},
    },
    comment: {
      commentTypeIconPaths: [],
      commentFeaturesBySourceType: {},
      commentTypes: [],
      commentTypeMap: {},
    },
    lov: {
      shareScopes: [],
      shareScopeMap: {},
      carKinds: [],
      carKindMap: {},
      roles: [],
    },
  });

  // refreshCommentType による更新を後続画面の resolve で巻き戻さないよう、反映は初回のみ行う
  let isMastersApplied = false;
  const resolveMastersPromise = async(): Promise<void> => {
    let resolved: MasterData;
    try {
      resolved = await mastersPromise;
    } catch (e) {
      console.error('マスターデータの取得に失敗しました', e);
      await router.push({ name: 'Login' });
      return;
    }
    if (isMastersApplied) return;
    isMastersApplied = true;
    masters.kp.kpMap = resolved.kpMap;
    masters.kp.kpMapByKpUid = resolved.kpMapByKpUid;
    masters.kp.roadNames = resolved.roadNames;
    masters.kp.roadNameDisps = resolved.roadNameDisps;
    masters.kp.roadNameDispMap = resolved.roadNameDispMap;
    masters.kp.roadNameToRoadNameDispMap = resolved.roadNameToRoadNameDispMap;
    masters.kp.geoConnections = resolved.geoConnections;
    masters.comment.commentTypeIconPaths = resolved.commentTypeIconPaths;
    masters.comment.commentFeaturesBySourceType = resolved.commentFeaturesBySourceType;
    masters.comment.commentTypes = resolved.commentType.vals;
    masters.comment.commentTypeMap = resolved.commentType.map;
    masters.lov.shareScopes = resolved.lovs.shareScope.vals;
    masters.lov.shareScopeMap = resolved.lovs.shareScope.map;
    masters.lov.carKinds = resolved.lovs.carKind.vals;
    masters.lov.carKindMap = resolved.lovs.carKind.map;
    masters.lov.roles = resolved.lovs.role.vals;
  };

  // 付箋種別の編集後、再読み込みなしで他画面（地図）へ反映するためのフック
  const refreshCommentType = (commentTypes: CommentType[]): void => {
    const built = buildCommentTypeValsMap(commentTypes);
    masters.comment.commentTypes = built.vals;
    masters.comment.commentTypeMap = built.map;
  };

  provide({ masters, resolveMastersPromise, refreshCommentType });
}

export function useReactiveMasters(): UseReactiveMastersContext {
  return inject();
}
